/**
 * Approved sends on the Google path (MA-484, ADR-0011 decision 6).
 *
 * Approving a draft no longer sends it there: comms-service answers 202
 * `{ status: 'approved', undo_until }` and orchestration dispatches the Gmail
 * draft when the undo window ends. Until then the person can Undo; afterwards
 * the row says what happened (sent, refused back to review, or an outcome
 * nobody can know without looking, which a person settles with Resolve).
 *
 * This module is the one place that reads an agent_drafts row and says which of
 * those it is (describeSend), and a small store of the sends this browser is
 * following (trackApprovedSend / useTrackedSends) that polls
 * GET /agent/drafts/:id until each one settles. The Nylas path answers 200
 * `{ status: 'sent' }` and keeps behaving as it did.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  commsDrafts,
  CommsHttpError,
  type AgentDraft,
  type ApproveDraftResult,
  type ResolveDraftAction,
} from '../../api/comms';

// ── Reading a row ────────────────────────────────────────────────────────────

export type SendPhase =
  /** Approved, the undo window is running: nothing has been sent. */
  | 'undo_window'
  /** The window is over (or dispatch has begun); the send is being made. */
  | 'sending'
  | 'sent'
  /** Back in pending_approval: undone, or refused at dispatch (see reason). */
  | 'returned'
  /** Rejected: nothing was sent. */
  | 'cancelled'
  /** dispatch.state 'uncertain': it may or may not have gone. Never resent automatically. */
  | 'uncertain'
  /** Approved with no record of a send (a Nylas send that failed). */
  | 'unknown';

export interface SendView {
  phase: SendPhase;
  /** Whole seconds left in the undo window; 0 outside it. */
  secondsLeft: number;
  canUndo: boolean;
  /** The Resolve actions (link / return / reject) apply: dispatch is 'uncertain'. */
  canResolve: boolean;
  /** Reconciliation has given up and handed the row to a person. */
  needsPerson: boolean;
  /** Why it is in this state, in words a person can read. */
  reason: string | null;
  sentAt: string | null;
  autoSent: boolean;
  /**
   * Sent, but the message Gmail sent is not the one approved (context.dispatch.
   * content_mismatch): it was changed in Gmail between the check and the send.
   */
  contentMismatch: boolean;
}

/** Nylas auto-sends (legacy 'auto_sent' rows) were undoable for 90 s after sent_at. */
const LEGACY_UNDO_MS = 90_000;

const REASONS: Record<string, string> = {
  draft_changed: 'The draft was changed in the mailbox after approval, so nothing was sent.',
  interrupted_during_send: 'The send was interrupted before Gmail answered.',
  dispatch_already_attempted: 'A send was already attempted for this approval.',
  dispatch_state_changed: 'The draft changed while it was being sent.',
  internal_error: 'comms-service hit an error while sending.',
  workflow_reports_sent_without_claim: 'The send workflow reports a send that comms-service has no record of.',
  timeout: 'Gmail did not answer in time.',
  network: 'The connection to Gmail failed during the send.',
};

/** The parts of a message a content check names, in words. */
const PART_WORDS: Record<string, string> = {
  to: 'recipients', cc: 'cc', bcc: 'bcc', subject: 'subject', body: 'text', parts: 'attachments',
};

/** Why a sent message is flagged as not the approved one, naming what differs when the check could tell. */
function contentMismatchReason(dispatch: AgentDraft['context']['dispatch']): string {
  const differs = (dispatch?.content_check?.differs ?? []).map(k => PART_WORDS[k]).filter(Boolean);
  const what = differs.length ? ` (changed: ${differs.join(', ')})` : '';
  return `It was changed in Gmail just before sending${what}. Check the sent message in Gmail.`;
}

/** A dispatch or refusal reason as a sentence; unknown codes are shown as they are. */
export function reasonText(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return REASONS[reason] ?? reason;
}

/** The server's undo deadline, in ms, or null when the row carries none. */
export function serverUndoUntilMs(draft: AgentDraft): number | null {
  const iso = draft.context?.approval?.undo_until;
  if (typeof iso !== 'string') return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/**
 * Why a draft that was approved is back in review, or null if it never was.
 * The last approval_history entry says how the approval ended ('undone', or a
 * dispatch refusal such as 'draft_changed'); approval_note carries the refusal's
 * sentence. An undo leaves approval_note as it was, so 'undone' is read first.
 */
export function returnedReason(draft: { approval_note?: string | null; context?: { approval_history?: unknown } | null }): string | null {
  const h = draft.context?.approval_history;
  if (!Array.isArray(h) || h.length === 0) return null;
  const last = h[h.length - 1] as { ended?: unknown };
  const ended = typeof last.ended === 'string' ? last.ended : null;
  if (ended === 'undone') return 'Undone: nothing was sent.';
  return draft.approval_note || reasonText(ended) || 'The approval ended without a send.';
}

/**
 * What a person should be told about this row now. `undoUntilMs` is a local
 * bound (the earlier of it and the server's undo_until wins), so a browser clock
 * running behind the server's never offers an Undo the server will refuse.
 */
export function describeSend(draft: AgentDraft, nowMs: number, opts?: { undoUntilMs?: number | null }): SendView {
  const ctx = draft.context ?? {};
  const autoSent = ctx.auto_sent === true;
  const base: SendView = {
    phase: 'unknown', secondsLeft: 0, canUndo: false, canResolve: false,
    needsPerson: false, reason: null, sentAt: draft.sent_at ?? null, autoSent, contentMismatch: false,
  };
  const status = draft.status as AgentDraft['status'] | 'auto_sent';
  // Sent: and, when the read-back found the sent message is not the approved one, say so.
  const sent = (): SendView => (ctx.dispatch?.content_mismatch === true
    ? { ...base, phase: 'sent', contentMismatch: true, reason: contentMismatchReason(ctx.dispatch) }
    : { ...base, phase: 'sent' });

  if (status === 'sent') return sent();
  if (status === 'rejected') return { ...base, phase: 'cancelled', reason: draft.approval_note ?? null };
  if (status === 'pending_approval' || status === 'user_draft') {
    return { ...base, phase: 'returned', reason: returnedReason(draft) ?? draft.approval_note ?? null };
  }
  if (status === 'auto_sent') {
    // A Nylas auto-send from before the CHECK constraint: sent at once with send_at.
    const sentAt = draft.sent_at ? Date.parse(draft.sent_at) : NaN;
    const left = Number.isFinite(sentAt) ? sentAt + LEGACY_UNDO_MS - nowMs : 0;
    return left > 0
      ? { ...base, phase: 'undo_window', secondsLeft: Math.ceil(left / 1000), canUndo: true }
      : { ...base, phase: 'sent' };
  }

  // status 'approved'
  const dispatch = ctx.dispatch;
  if (dispatch?.state === 'sent') return sent();
  if (dispatch?.state === 'uncertain') {
    return {
      ...base,
      phase: 'uncertain',
      canResolve: true,
      needsPerson: dispatch.needs_review === true || dispatch.reconcile?.needs_review === true,
      reason: reasonText(dispatch.reason) ?? 'Nobody can tell yet whether this message went.',
    };
  }
  if (dispatch) return { ...base, phase: 'sending' };

  const server = serverUndoUntilMs(draft);
  const local = opts?.undoUntilMs ?? null;
  const deadline = server !== null && local !== null ? Math.min(server, local) : (server ?? local);
  if (deadline === null) {
    return { ...base, phase: 'unknown', reason: 'Approved, but there is no record of a send.' };
  }
  if (nowMs < deadline) {
    return { ...base, phase: 'undo_window', secondsLeft: Math.ceil((deadline - nowMs) / 1000), canUndo: true };
  }
  return { ...base, phase: 'sending' };
}

/** The status line for a phase. */
export function phaseLabel(view: SendView): string {
  switch (view.phase) {
    case 'undo_window': return `Sending in ${view.secondsLeft}s`;
    case 'sending': return 'Sending…';
    case 'sent': return view.contentMismatch ? 'Sent, but the message Gmail sent differs from what was approved' : 'Sent';
    case 'returned': return 'Not sent: back for review';
    case 'cancelled': return 'Cancelled: nothing was sent';
    case 'uncertain': return view.needsPerson ? 'Outcome unknown: needs a person' : 'Outcome unknown';
    case 'unknown': return 'Approved: no send record';
  }
}

export function isSettled(view: SendView): boolean {
  return view.phase === 'sent' || view.phase === 'returned' || view.phase === 'cancelled';
}

// ── Actions shared by every surface ──────────────────────────────────────────

/**
 * comms-service's refusals of an edit, approve or decline, as sentences a person
 * can read. Its error field is a code ('draft_changed_in_mailbox') or a short
 * technical message ('Draft not found or not pending'); neither is shown raw.
 */
const ERROR_TEXT: Record<string, string> = {
  draft_changed_in_mailbox: 'The draft was changed in Gmail after it was opened here.',
  mailbox_draft_has_attachments: "Martha's draft has attachments in Gmail, which cannot be changed from here.",
  draft_has_no_mailbox: "Martha's draft is not linked to a mailbox.",
  mailbox_not_on_google: 'This mailbox is not connected through Google.',
  mailbox_draft_missing: "Martha's draft is no longer in Gmail; it may have been sent or deleted there.",
  mailbox_unavailable: 'Gmail could not be reached.',
  not_your_mailbox: 'You cannot act for this mailbox.',
  send_scheduler_unavailable: 'The send could not be scheduled.',
  approval_ended_during_approve: 'The approval ended while it was being made.',
  reply_target_unavailable: 'The message being replied to could not be found in Gmail.',
  provider_has_no_drafts: 'This mailbox cannot keep drafts.',
  provider_has_no_draft_send: 'This mailbox cannot send drafts.',
  'Draft not found': "Martha's draft no longer exists.",
  'Draft not found or not pending': "Martha's draft is no longer waiting for approval; it may have been sent or declined elsewhere.",
};

const STATUS_TEXT: Record<string, string> = {
  approved: 'This draft has already been approved.',
  sent: 'This draft has already been sent.',
  auto_sent: 'This draft has already been sent.',
  rejected: 'This draft has already been declined.',
};

/** 'Draft is sent, not pending_approval' (both paths) names the status the draft is in. */
const NOT_PENDING = /^Draft is (\w+), not pending_approval$/;

/** The status a refusal says the draft is already in, when it says one. */
function refusedStatus(err: CommsHttpError): string | undefined {
  if (typeof err.body?.status === 'string') return err.body.status;
  return NOT_PENDING.exec(err.code ?? '')?.[1];
}

/** A comms-service refusal in words a person can read: never a raw code or JSON body. */
export function errorText(err: unknown): string {
  if (err instanceof CommsHttpError) {
    const code = err.code;
    if (code && ERROR_TEXT[code]) return ERROR_TEXT[code];
    if (code && NOT_PENDING.test(code)) {
      const status = refusedStatus(err) ?? '';
      return STATUS_TEXT[status] ?? "Martha's draft is no longer waiting for approval.";
    }
    const msg = typeof err.body?.message === 'string' ? err.body.message : null;
    if (msg) return msg;
    // An unknown snake_case code is not a sentence: say who refused, not the code.
    if (code && /^[a-z0-9]+(_[a-z0-9]+)+$/.test(code)) return `Martha's mail service refused it (HTTP ${err.status}).`;
    return code ?? (err.message || `HTTP ${err.status}`);
  }
  // fetch rejects with a TypeError when there is no answer at all.
  if (err instanceof TypeError) return "Martha's mail service could not be reached.";
  return (err as Error)?.message || "Martha's mail service did not answer.";
}

/**
 * A refused edit (PATCH) after which the person's reply may go as a new email:
 * the draft was not changed and nothing was sent. 409 mailbox_draft_has_attachments
 * (a rebuild from the reply's text would drop them) or 422 draft_has_no_mailbox.
 */
export function editRefusalAllowsPlainSend(err: unknown): boolean {
  return err instanceof CommsHttpError && (
    (err.status === 409 && err.code === 'mailbox_draft_has_attachments')
    || (err.status === 422 && err.code === 'draft_has_no_mailbox'));
}

/**
 * A refused approve after which the person's reply may go as a new email. Any 4xx
 * means this approval scheduled nothing, unless the refusal says the draft is
 * already approved or sent (elsewhere): then a new email would be the second
 * message. A 5xx or no answer may have scheduled the send, so never.
 */
export function approveRefusalAllowsPlainSend(err: unknown): boolean {
  if (!(err instanceof CommsHttpError) || err.status < 400 || err.status >= 500) return false;
  const status = refusedStatus(err);
  return status !== 'approved' && status !== 'sent' && status !== 'auto_sent';
}

/**
 * 409 draft_changed_in_mailbox: the draft was edited in Gmail, comms-service took
 * Gmail's version into the row and approved nothing. `pulled` is that version (the
 * 409 body's draft), or null when comms-service could not read it.
 */
export function draftChangedInMailbox(err: unknown): { pulled: AgentDraft | null } | null {
  if (!(err instanceof CommsHttpError) || err.status !== 409 || err.code !== 'draft_changed_in_mailbox') return null;
  const d = err.body?.draft;
  return { pulled: d && typeof d === 'object' && typeof (d as AgentDraft).id === 'string' ? d as AgentDraft : null };
}

export type ActionOutcome =
  | { ok: true; draft: AgentDraft | null; text: string }
  | { ok: false; text: string; refresh: boolean };

/** Undo, with each refusal turned into words. A failed undo is never reported as success. */
export async function undoApprovedSend(id: string): Promise<ActionOutcome> {
  try {
    const r = await commsDrafts.undo(id);
    // Nylas answers { ok, reason } with no draft; the caller re-reads the row.
    const draft = (r as { draft?: AgentDraft }).draft ?? null;
    const text = draft?.status === 'rejected' ? 'Undone: cancelled, nothing was sent.' : 'Undone: nothing was sent.';
    return { ok: true, draft, text };
  } catch (err) {
    if (err instanceof CommsHttpError) {
      if (err.status === 409 && err.code === 'too_late') {
        const phase = typeof err.body?.phase === 'string' ? ` (${err.body.phase})` : '';
        return { ok: false, text: `Undo failed: too late, the send had already started${phase}.`, refresh: true };
      }
      if (err.status === 409 && err.code === 'not_undoable') {
        return { ok: false, text: 'Undo failed: this message is no longer waiting to send (it may already have gone).', refresh: true };
      }
      if (err.status === 403) return { ok: false, text: 'Undo failed: this draft is not yours to undo.', refresh: false };
      if (err.status === 400 && err.code) return { ok: false, text: `Undo failed: ${err.code}`, refresh: true };
    }
    return { ok: false, text: `Undo failed: ${errorText(err)}`, refresh: true };
  }
}

export async function resolveUncertainSend(id: string, action: ResolveDraftAction): Promise<ActionOutcome> {
  try {
    const r = await commsDrafts.resolve(id, action);
    const text = r.outcome === 'linked' ? 'Marked as sent.'
      : r.outcome === 'returned' ? 'Returned for review: nothing more will be sent.'
      : 'Rejected.';
    return { ok: true, draft: r.draft, text };
  } catch (err) {
    if (err instanceof CommsHttpError) {
      if (err.status === 409 && err.code === 'draft_gone_use_link_or_reject') {
        return {
          ok: false,
          text: 'The Gmail draft is gone (sending deletes it), so it cannot be returned. Mark it as sent with the message id, or reject it.',
          refresh: false,
        };
      }
      if (err.status === 409 && err.code === 'not_resolvable') {
        return { ok: false, text: 'This send no longer needs a decision.', refresh: true };
      }
      if (err.status === 403) return { ok: false, text: 'This draft is not yours to resolve.', refresh: false };
    }
    return { ok: false, text: `Resolve failed: ${errorText(err)}`, refresh: true };
  }
}

// ── The thread's pending agent draft ─────────────────────────────────────────

/**
 * The pending agent draft that answers this thread in THIS mailbox, if any.
 * Replying with it approves it, and an approved draft is sent from its own
 * mailbox, so a draft of another mailbox on the same thread id is not offered.
 */
export function pendingDraftForThread<D extends Pick<AgentDraft, 'connection_id' | 'context'>>(
  drafts: readonly D[] | undefined,
  threadId: string,
  connectionId: string,
): D | undefined {
  return drafts?.find(d =>
    (d.context?.thread_id === threadId || d.context?.threadId === threadId)
    && (d.connection_id == null || d.connection_id === connectionId));
}

// ── The sends this browser is following ──────────────────────────────────────

export type SendOrigin =
  /** Shown in the floating tray (the inbox reply, which closes when it is sent). */
  | 'tray'
  /** Shown inline by the page that made it (Martha Mind). */
  | 'inline';

export interface TrackedSend {
  id: string;
  draft: AgentDraft;
  /** The earlier of the server's undo_until and receipt + undo_seconds. */
  undoUntilMs: number | null;
  origin: SendOrigin;
  notice: { kind: 'error' | 'info'; text: string } | null;
  busy: 'undo' | 'resolve' | null;
  lastFetchedAt: number;
  /** When this browser first read the row as sent (the content check lands after that). */
  sentSeenAt?: number;
}

type Listener = () => void;

let entries: TrackedSend[] = [];
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l();
}

function setEntries(next: TrackedSend[]) {
  entries = next;
  emit();
}

function patch(id: string, update: Partial<TrackedSend>) {
  setEntries(entries.map(e => (e.id === id ? { ...e, ...update } : e)));
}

export function getTrackedSends(): readonly TrackedSend[] {
  return entries;
}

/** Follow a send the person just approved. Nylas results (already sent) are shown as Sent. */
export function trackApprovedSend(result: ApproveDraftResult, origin: SendOrigin, receivedAtMs = Date.now()): void {
  const draft = result.draft;
  let undoUntilMs: number | null = null;
  if (result.status === 'approved') {
    const server = Date.parse(result.undo_until);
    const local = typeof result.undo_seconds === 'number' ? receivedAtMs + result.undo_seconds * 1000 : NaN;
    const both = [server, local].filter(Number.isFinite);
    undoUntilMs = both.length ? Math.min(...both) : null;
  }
  const entry: TrackedSend = {
    id: draft.id,
    // The 202 carries the draft as approved; make sure the row reads that way.
    draft: result.status === 'approved' && draft.status !== 'approved'
      ? { ...draft, status: 'approved', context: { ...draft.context, approval: { ...(draft.context?.approval ?? {}), operation_id: result.operation_id, undo_until: result.undo_until } } }
      : draft,
    undoUntilMs,
    origin,
    notice: null,
    busy: null,
    lastFetchedAt: receivedAtMs,
  };
  setEntries([entry, ...entries.filter(e => e.id !== draft.id)]);
}

/** Follow approved rows read from the server (sends made elsewhere), without duplicating any. */
export function ingestApprovedDrafts(drafts: AgentDraft[], origin: SendOrigin, nowMs = Date.now()): void {
  let changed = false;
  const next = [...entries];
  for (const d of drafts) {
    const i = next.findIndex(e => e.id === d.id);
    if (i === -1) {
      next.push({ id: d.id, draft: d, undoUntilMs: null, origin, notice: null, busy: null, lastFetchedAt: nowMs });
      changed = true;
    } else if (d.updated_at !== next[i].draft.updated_at || d.status !== next[i].draft.status) {
      next[i] = { ...next[i], draft: d, lastFetchedAt: nowMs };
      changed = true;
    }
  }
  if (changed) setEntries(next);
}

export function dismissTrackedSend(id: string): void {
  setEntries(entries.filter(e => e.id !== id));
}

/** Test seam: forget everything. */
export function resetTrackedSends(): void {
  setEntries([]);
}

export async function refreshTrackedSend(id: string): Promise<void> {
  const now = Date.now();
  patch(id, { lastFetchedAt: now });
  try {
    const draft = await commsDrafts.get(id);
    const entry = entries.find(e => e.id === id);
    if (entry) {
      const seen = draft.status === 'sent' && entry.sentSeenAt === undefined ? { sentSeenAt: Date.now() } : {};
      patch(id, { draft, lastFetchedAt: Date.now(), ...seen });
    }
  } catch (err) {
    if (err instanceof CommsHttpError && err.status === 404) {
      patch(id, { notice: { kind: 'error', text: 'This draft no longer exists.' } });
    }
    // Anything else: try again on the next poll.
  }
}

export async function undoTrackedSend(id: string): Promise<void> {
  patch(id, { busy: 'undo', notice: null });
  const r = await undoApprovedSend(id);
  if (r.ok) {
    patch(id, { busy: null, notice: { kind: 'info', text: r.text }, ...(r.draft ? { draft: r.draft } : {}) });
    if (!r.draft) await refreshTrackedSend(id);
  } else {
    patch(id, { busy: null, notice: { kind: 'error', text: r.text } });
    if (r.refresh) await refreshTrackedSend(id);
  }
}

export async function resolveTrackedSend(id: string, action: ResolveDraftAction): Promise<void> {
  patch(id, { busy: 'resolve', notice: null });
  const r = await resolveUncertainSend(id, action);
  if (r.ok) {
    patch(id, { busy: null, notice: { kind: 'info', text: r.text }, ...(r.draft ? { draft: r.draft } : {}) });
  } else {
    patch(id, { busy: null, notice: { kind: 'error', text: r.text } });
    if (r.refresh) await refreshTrackedSend(id);
  }
}

// ── Polling ──────────────────────────────────────────────────────────────────

/** How often the poller looks; each phase has its own refetch interval below. */
export const POLL_TICK_MS = 1_000;
const REFETCH_MS: Partial<Record<SendPhase, number>> = {
  sending: 2_000,      // the window is over: the outcome is due any moment
  undo_window: 10_000, // catches an undo made from another surface
  uncertain: 15_000,   // a person or reconciliation may settle it
  unknown: 15_000,
};

/**
 * A Google send that has gone is read back and compared with the approval a moment
 * AFTER the row turns 'sent' (context.dispatch.content_check, up to comms-service's
 * 10 s read timeout). Until that lands, 'sent' is re-read, so a mismatch is shown
 * rather than a plain 'Sent' that stopped looking.
 */
const CONTENT_CHECK_REFETCH_MS = 2_000;
const CONTENT_CHECK_WAIT_MS = 30_000;

function awaitingContentCheck(e: TrackedSend, now: number): boolean {
  const dispatch = e.draft.context?.dispatch;
  if (e.draft.status !== 'sent' || dispatch?.state !== 'sent' || dispatch.content_check) return false;
  return e.sentSeenAt === undefined || now - e.sentSeenAt < CONTENT_CHECK_WAIT_MS;
}

let pollers = 0;
let timer: ReturnType<typeof setInterval> | null = null;

function pollOnce() {
  const now = Date.now();
  for (const e of entries) {
    if (e.busy) continue;
    const view = describeSend(e.draft, now, { undoUntilMs: e.undoUntilMs });
    const every = REFETCH_MS[view.phase] ?? (awaitingContentCheck(e, now) ? CONTENT_CHECK_REFETCH_MS : undefined);
    if (every !== undefined && now - e.lastFetchedAt >= every) void refreshTrackedSend(e.id);
  }
}

function startPolling(): () => void {
  pollers += 1;
  if (!timer) timer = setInterval(pollOnce, POLL_TICK_MS);
  return () => {
    pollers -= 1;
    if (pollers === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

function subscribe(l: Listener) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

/** The tracked sends (optionally for one origin), polled while a component shows them. */
export function useTrackedSends(origin?: SendOrigin): readonly TrackedSend[] {
  const all = useSyncExternalStore(subscribe, getTrackedSends, getTrackedSends);
  useEffect(() => startPolling(), []);
  return origin ? all.filter(e => e.origin === origin) : all;
}

/** Date.now(), re-rendered every `ms` while `active`, so countdowns move. */
export function useNow(ms = 1_000, active = true): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    // Catch up at once on becoming active (the last reading may be long stale), then tick.
    const first = setTimeout(() => setNow(Date.now()), 0);
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => { clearTimeout(first); clearInterval(t); };
  }, [ms, active]);
  return now;
}
